
import { Component, Ref, Vue } from 'vue-property-decorator'
import AcademyTeacherList from '@/components/templates/manage/AcademyTeacherList.vue'
import ModalRegistTeacher from '@/views/modal/manage/ModalRegistTeacher.vue'
import ModalEditTeacher from '@/views/modal/manage/ModalEditTeacher.vue'
import ModalEditPassword from '@/views/modal/manage/ModalEditPassword.vue'
import { UserList } from '@/models/manage/UserList'
import { SelectItem } from '@/models/SelectItem'
import { BranchesApi } from '@/api/methods/manage/BranchesApi'
import { UserBranchesApi } from '@/api/methods/manage/UserBranchesApi'
import { UsersApi } from '@/api/methods/manage/UsersApi'

/**
 * 先生一覧
 */
@Component({
  components: {
    AcademyTeacherList,
    ModalRegistTeacher,
    ModalEditTeacher,
    ModalEditPassword,
  },
})
export default class AcademyTeachers extends Vue {
  /** 先生一覧レイアウト */
  @Ref()
  academyTeacherList!: AcademyTeacherList

  /** 先生情報登録モーダル */
  @Ref()
  modalRegistTeacher!: ModalRegistTeacher

  /** 先生情報編集モーダル */
  @Ref()
  modalEditTeacher!: ModalEditTeacher

  /** 先生パスワード編集モーダル */
  @Ref()
  modalEditPassword!: ModalEditPassword

  /** 1ページの最大件数 */
  private readonly MAX_PER_PAGE = 7

  /** 教室フィルターの固定値「全て」 */
  private readonly OPTION_ALL = { value: '', label: '全て' }

  /** ユーザ削除時の確認メッセージ */
  private readonly MESSAGE_CONFIRM_DELETE_USER = '削除しますか？'

  /** 先生一覧 */
  private teacherList: UserList[] = []

  /** 先生人数 */
  private teacherNumber = 0

  /** 教室フィルターの選択肢 */
  private filterBranchList: SelectItem[] = []

  /** ページ数 */
  private pageLength = 0

  /** 現在のページ */
  private currentPage = 1

  /**
   * 先生更新後の処理(登録・編集モーダル内で呼び出し)
   *   呼び出し元でローディング処理を行う
   */
  private async afterUpdateTeacher(): Promise<void> {
    // 先生一覧取得
    await this.getTeacherList()
  }

  /**
   * 初期処理
   */
  private async mounted(): Promise<void> {
    this.$loading.start()

    // 教室一覧取得(フィルター設定)
    await this.getBranchList()

    // 先生一覧取得
    await this.getTeacherList()

    this.$loading.complete()
  }

  /**
   * 教室一覧取得(フィルター設定)
   */
  private async getBranchList(): Promise<void> {
    // 教室一覧取得
    const branches = await BranchesApi.getBranches()

    // 教室フィルター設定
    this.filterBranchList = branches.map((branch) => {
      return {
        // 教室IDをコードとして使用
        value: String(branch.id),
        label: branch.name,
      }
    })

    // 教室フィルターの先頭に固定値を設定
    this.filterBranchList.unshift(this.OPTION_ALL)

    // 教室フィルターの初期選択値を設定
    this.academyTeacherList.setCurrentBranch(this.filterBranchList[0].value)
  }

  /**
   * 先生一覧取得
   */
  private async getTeacherList(): Promise<void> {
    // 非必須のパラメータを定義
    let options: UserBranchesApi.optionParams = {}

    // 教室指定があれば追加
    if (this.currentBranch()) {
      options.branchId = Number(this.currentBranch())
    }

    // 名前検索指定があれば追加
    if (this.textInputedSearch()) {
      options.searchText = this.textInputedSearch()
    }

    // 先生一覧取得
    const userBranches = await UserBranchesApi.getTeachers(options, this.currentPage, this.MAX_PER_PAGE)

    // 先生の人数を設定
    this.teacherNumber = userBranches.totalCount

    const divideNumber = Math.floor(this.teacherNumber / this.MAX_PER_PAGE)
    this.pageLength = this.teacherNumber % this.MAX_PER_PAGE ? divideNumber + 1 : divideNumber

    // 先生一覧を表示用データに設定
    this.teacherList = userBranches.teachers.map((teacher) => {
      return {
        userId: teacher.userId,
        avatarIconCode: teacher.avatarIconCode,
        nickname: teacher.nickname,
        loginId: teacher.loginId,
        branchName: teacher.branchName,
      }
    })

    // 先生の人数を設定
    this.teacherNumber = userBranches.totalCount
  }

  /**
   * 選択中の教室
   *
   * @returns 教室コード(ID)
   */
  private currentBranch(): string | null {
    return this.academyTeacherList.currentBranch()
  }

  /**
   * 検索欄に入力された文字列
   *
   * @returns 入力された文字列
   */
  private textInputedSearch(): string {
    return this.academyTeacherList.textInputedSearch()
  }

  /**
   * 教室フィルター変更時のイベント
   */
  private async changeFilter(): Promise<void> {
    this.$loading.start()

    // 先生一覧取得
    await this.getTeacherList()

    this.$loading.complete()
  }

  /**
   * 先生氏名検索
   */
  private async searchNameTeacher(): Promise<void> {
    this.$loading.start()

    // 先生一覧取得
    await this.getTeacherList()

    this.$loading.complete()
  }

  /**
   * 削除ボタン押下時のイベント
   *
   * @param userId ユーザID
   */
  private async eventClickDeleteButton(userId: number): Promise<void> {
    // 誤操作による削除防止
    const conf = confirm(this.MESSAGE_CONFIRM_DELETE_USER)
    if (!conf) {
      return
    }

    this.$loading.start()

    // ユーザ削除
    await UsersApi.deleteUser(userId)

    // 先生一覧取得
    await this.getTeacherList()

    this.$loading.complete()
  }

  /**
   * ユーザ登録ボタン押下時のイベント
   */
  private async eventClickCreateUserButton(): Promise<void> {
    await this.modalRegistTeacher.show()
  }

  /**
   * ユーザデータ編集ボタン押下時のイベント
   *
   * @param userId ユーザID
   */
  private async eventClickEditUserDataButton(userId: number): Promise<void> {
    await this.modalEditTeacher.show(userId)
  }

  /**
   * パスワード編集ボタン押下時のイベント
   *
   * @param userId ユーザID
   */
  private eventClickEditPasswordButton(userId: number): void {
    this.modalEditPassword.show(userId)
  }

  /**
   * ページリンク押下時の処理
   *
   * @param value ページ番号/戻る/進む
   */
  private async changePage(value: number): Promise<void> {
    this.$loading.start()

    // 現在のページを更新
    this.currentPage = value

    // 先生一覧取得
    await this.getTeacherList()

    this.$loading.complete()
  }
}
